/**
 * @fileoverview Input/output schemas for character-modifier-v1.
 *
 * This agent takes a natural language change request from the author and
 * applies modifications to a single character's core attributes (initial
 * state) and their planned character arc. The output includes the modified
 * attributes and the updated arc plus commentary explaining the changes.
 *
 * This is a Tier 2 agent: the result is shown to the user for confirmation
 * before being persisted.
 */

import { z } from 'zod';
import { BaseFlowInputSchema } from '@/ai/agents/base-schema';
import { AGENT_SCHEMA_OBJECTS } from '@/ai/agents/prompt-variables';
import { CharacterAttributesSchema } from '@/domains/characters/schemas/character-sub-entities-v2';
import { StructurePhaseSchema } from '@/domains/story-planning/schemas/structure-phases';

// =============================================================================
// FLOW INPUT SCHEMA
// =============================================================================

export const InputSchema = BaseFlowInputSchema
  .merge(AGENT_SCHEMA_OBJECTS.activeCharacterContext.schema)
  .merge(AGENT_SCHEMA_OBJECTS.storyContext.schema)
  .merge(AGENT_SCHEMA_OBJECTS.characters.schema)
  .extend({
    changeRequest: z.string().describe(
      'The author\'s natural language description of the changes they want applied to this character. ' +
      'This can reference core attributes (personality, physical description, plot role, etc.) and/or ' +
      'the character arc (arc name, summary, beats).'
    ),
    currentPlannedArc: z.any().optional().describe(
      'The character\'s current planned arc (if any), including arcName, arcSummary, and beats.'
    ),
    isShortForm: z.boolean().optional().describe(
      'When true, the project is short-form. The character arc should be summary-only with an empty beats array.'
    ),
  });

export type Input = z.infer<typeof InputSchema>;

// =============================================================================
// OUTPUT SCHEMA
// =============================================================================

// Local arc schema with loosened ID constraints so model-generated IDs for new
// beats don't fail strict UUID validation. IDs are normalized (to real UUIDs
// if missing) by the consuming hook before persistence.
const ModifiedCharacterBeatSchema = z.object({
  id: z.string().describe(
    'Beat ID. Preserve existing IDs for unchanged beats; use a new UUID (v4) for new beats.'
  ),
  globalPhase: StructurePhaseSchema.optional().describe(
    'Where this beat sits in the OVERALL story timeline.'
  ),
  arcPhase: StructurePhaseSchema.optional().describe(
    'The structural function this beat serves for THIS character\'s internal arc.'
  ),
  internalState: z.string().describe(
    'The character\'s emotional realization, decision, or psychological shift at this moment.'
  ),
  behavior: z.string().optional().describe(
    'The observable action or behavior triggered by this internal shift.'
  ),
  arcProgress: z.number().min(-1).max(1).describe(
    'Position on the truth/lie spectrum (-1 to +1).'
  ),
});

const ModifiedPlannedArcSchema = z.object({
  id: z.string().describe(
    'Arc ID. Preserve the existing arc id when modifying; generate a new UUID only for a brand-new arc.'
  ),
  arcName: z.string().describe('A short, evocative title for the character\'s arc.'),
  arcSummary: z.string().describe('A 1-3 sentence summary of the character\'s overall transformation.'),
  beats: z.array(ModifiedCharacterBeatSchema).describe(
    'The sequence of internal shifts that define this character\'s arc. ' +
    'Empty array for short-form projects.'
  ),
});

export const OutputSchema = z.object({
  commentary: z.string().optional().describe(
    'A summary explaining what changes were made to the character\'s attributes and/or arc and why. ' +
    'Be specific about which fields were touched. Explain how the changes maintain psychological ' +
    'and narrative coherence.'
  ),
  characterName: z.string().describe(
    'The character\'s name after modification. Usually unchanged unless the change request asks for a rename.'
  ),
  category: z.enum(['major', 'minor', 'extra']).describe(
    'The character\'s importance level after modification.'
  ),
  modifiedAttributes: CharacterAttributesSchema.partial().strict().describe(
    'An object containing only the character attributes that were changed. ' +
    'Omit unchanged properties. Include plotRole if it was modified. ' +
    'Only the canonical character attribute keys are allowed — do NOT introduce new keys.'
  ),
  plannedCharacterArc: ModifiedPlannedArcSchema.optional().describe(
    'The complete updated planned character arc. ' +
    'Omit if the arc was not touched by the change request. ' +
    'Preserve beat IDs for unchanged beats; generate new UUIDs for new beats. ' +
    'For short-form projects, beats should be an empty array.'
  ),
});

export type Output = z.infer<typeof OutputSchema>;
